import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectionPickerDialog, type SelectionValue } from '@/components';
import { useCompanies } from '../hooks';
import type { Company } from '../types';
import useDebounce from '@/utils/useDebounce';

const PICKER_PAGE_SIZE = 25;

interface CompanyPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (company: Company) => void;
  selectedCompanyId?: string;
}

/**
 * Dialog that lets an admin search the companies he has access to and pick one.
 * Reuses the same company list endpoint as the companies overview page.
 */
export function CompanyPickerDialog({
  isOpen,
  onClose,
  onSelect,
  selectedCompanyId,
}: CompanyPickerDialogProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput);

  // The dialog stays mounted while closed, so the list is only fetched once it opens.
  const { data, isLoading } = useCompanies(
    { page: 1, pageSize: PICKER_PAGE_SIZE, search: debouncedSearch || undefined },
    { enabled: isOpen }
  );

  const values: SelectionValue<Company>[] = useMemo(
    () =>
      (data?.data ?? []).map((company) => ({
        id: company.id,
        value: company.name,
        subValue: company.level?.name,
        obj: company,
      })),
    [data]
  );

  const handleSelect = (_id: string, company?: Company) => {
    if (!company) return;
    onSelect(company);
    onClose();
  };

  return (
    <SelectionPickerDialog<Company>
      isOpen={isOpen}
      onClose={onClose}
      header={t('companies.picker.title')}
      subheader={t('companies.picker.description')}
      isLoading={isLoading}
      values={values}
      isUpdating={false}
      onSelect={handleSelect}
      selectedId={selectedCompanyId}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('companies.picker.searchPlaceholder')}
      emptyText={t('companies.picker.noResults')}
    />
  );
}
